import log from 'loglevel'

/**
 * 屋内関連データ Vuex Store
 */
export default {
  name: 'mapIndoor',
  namespaced: true,
  state: {
    indoorSite: {},
    activeIndoorBuildingId: null,
    indoorBuildings: [],
    indoorLevels: [],
    activeIndoorLevelId: null,
    isClickBuildingPinToActive: true, // ピンをクリック時にアクティブにするか？
  },
  persistentStates: [
    'indoorSites',
    'indoorBuildings',
    'indoorLevels',
    'activeIndoorBuildingId',
    'activeIndoorLevelId',
  ],
  mutations: {
    /**
     * stateを初期化する
     * @param {*} state
     */
    resetState(state) {
      state.indoorSite = {}
      state.activeIndoorBuildingId = null
      state.indoorBuildings = []
      state.indoorLevels = []
      state.activeIndoorLevelId = null
      state.isClickBuildingPinToActive = true
    },
    /**
     * IndoorBuilding の設定
     * @param {*} state
     * @param {Array.<Object>} buildings
     */
    setIndoorBuildings(state, buildings) {
      state.indoorBuildings = buildings
    },
    /**
     * IndoorLevels の設定
     * @param {*} state
     * @param {Array.<Object>} levels
     */
    setIndoorLevels(state, levels) {
      state.indoorLevels = levels
    },
    /**
     * activeIndoorLevelId の設定
     * @param {*} state
     * @param {number} activeIndoorLevelId
     */
    setActiveIndoorLevelId(state, activeIndoorLevelId) {
      state.activeIndoorLevelId = activeIndoorLevelId
    },
    /**
     * IndoorLevels の設定
     * @param {*} state
     * @param {number} activeIndoorLevelId
     */
    setActiveIndoorBuildingId(state, activeIndoorBuildingId) {
      state.activeIndoorBuildingId = activeIndoorBuildingId
    },
    /**
     * 建物ピンクリックで、建物アクティブにするか
     * @param {*} state
     * @param {boolean} active アクティブにする
     */
    setClickBuildingPinToActive(state, active) {
      state.isClickBuildingPinToActive = active
    },
  },
  actions: {
    /**
     * stateをリセットする
     */
    resetState({ commit }) {
      commit('resetState')
    },
    /**
     * 建物が変更された
     * @param {Object} building
     */
    changeIndoorBuilding({ state, commit, dispatch }, building) {
      if (!building) {
        // 建物を未選択状態にする
        dispatch('unselectedBuilding')
        return
      }

      commit('setIndoorBuildings', [building])
      commit('setActiveIndoorBuildingId', building.id)
      commit('setIndoorLevels', building.indoor_levels)
      if (!building.indoor_levels || !building.indoor_levels.length) return

      if (
        !building.indoor_levels.find((l) => l.id === state.activeIndoorLevelId)
      ) {
        // 建物内にアクティブレベルが含まれていない場合はデフォルトレベルを選択する
        const activeLevel =
          building.indoor_levels.find((v) => v.default_level) ||
          building.indoor_levels[0]
        commit('setActiveIndoorLevelId', activeLevel.id)
      }
    },
    /**
     * 建物が未選択状態になった
     */
    unselectedBuilding({ commit, state }) {
      // リアクティブが走らないように、空の状態の場合は更新しない
      if (!state.indoorBuildings || state.indoorBuildings.length !== 0) {
        commit('setIndoorBuildings', [])
      }
      if (!state.indoorLevels || state.indoorLevels.length !== 0) {
        commit('setIndoorLevels', [])
      }
      commit('setActiveIndoorBuildingId', null)

      // レベルも未選択状態にする (すでになっているかも知れないが。。。)
      commit('setActiveIndoorLevelId', null)
    },
    /**
     * Active な IndoorLevelId が変更された
     * @param {number} levelId
     * ! 選択解除 levelId = null 以下の順番でイベントが飛ぶ
     * activeLevelId (null)
     *   => setIndoorBuildings([])
     *   => levels ([])
     *   => setActiveIndoorBuildingId (null)
     *
     * ! bottom up で、建物の選択が解除
     * 最終的に建物の選択がなくなり、グローバルモードへ戻る
     */
    setActiveIndoorLevelId(
      { commit, dispatch, getters, rootGetters },
      levelId
    ) {
      if (!levelId) {
        commit('setActiveIndoorLevelId', levelId)
        // レベルの選択がなくなったので、建物も未選択状態にする
        dispatch('unselectedBuilding')
        return
      }

      // フロア存在チェック
      const targetIndoorLevel =
        rootGetters['indoorLevel/indoorLevelMap'].get(levelId)
      // 無ければ変更できないので何もしない
      if (!targetIndoorLevel) {
        log.warn('IndoorLevel Not Found! id:', levelId)
        return
      }

      // 紐づいているビルを取得
      const indoorBuildings = rootGetters[
        'indoorBuilding/indoorBuildings'
      ].filter((b) => b.indoor_levels.some((l) => l.id === levelId))
      // 何も紐づいていなければフロア変更できないので何もしない
      if (!indoorBuildings.length) {
        log.warn('IndoorLevel is related to no buildings! id:', levelId)
        return
      }

      commit('setActiveIndoorLevelId', levelId)

      // フロアがアクティブなビルに紐づいていなければビル選択を紐づいているビルに変更する
      // 対象のフロアがデフォルトになっているビルがあればそのビル、無ければ紐づいているビルの先頭
      const isContainedActiveBuilding = indoorBuildings.some(
        (b) => b.id === getters.activeIndoorBuildingId
      )
      if (!isContainedActiveBuilding) {
        const targetIndoorBuilding =
          indoorBuildings.find((b) =>
            b.indoor_levels.some((l) => l.id === levelId && l.default_level)
          ) || indoorBuildings[0]
        dispatch('changeIndoorBuilding', targetIndoorBuilding)
      }
    },
  },
  getters: {
    /**
     * マップのグローバルモードか？ (建物非選択モード)
     * @param {*} state
     * @returns {boolean} true - グローバルモード (建物非選択)
     */
    isGlobalMode(state) {
      return !state.activeIndoorBuildingId
    },
    activeIndoorBuilding(state) {
      return state.indoorBuildings.find(
        (v) => v.id === state.activeIndoorBuildingId
      )
    },
    activeIndoorBuildingId(state) {
      return state.activeIndoorBuildingId
    },
    activeIndoorLevel(state) {
      return state.indoorLevels.find((v) => v.id === state.activeIndoorLevelId)
    },
    activeIndoorLevelId(state) {
      return state.activeIndoorLevelId
    },
    indoorLevels(state) {
      return state.indoorLevels
    },
    indoorBuildings(state) {
      return state.indoorBuildings
    },
  },
}
